import React, { useState } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { Box } from "rebass/styled-components";
import styled, { css } from "styled-components";
import Button from "../Button";
import Fixtures from "../Fixtures";
import { ControlArrowRight } from "../icons/Arrows";
import { Main, Wrapper } from "../Layout";
import PageHeader from "../PageHeader";
import CreateSquad, { IListTableProps } from "./CreateSquad";
import ElementList from "./ElementList";

const PusherWrap = styled.div`
  overflow-x: hidden;
`;

const Pusher = styled.div<IState>`
  position: relative;
  transition: all 300ms;

  ${(props) =>
    props.isSidebarVisible &&
    css`
      transform: translate3d(-100%, 0, 0);

      @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
        transform: none;
      }
    `}
`;

const PusherSecondary = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  transform: translate3d(100%, 0, 0);
  overflow-y: auto;
  padding-top: 2rem;

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    position: static;
    transform: none;
    width: calc(100% / 3);
    padding-right: 1rem;
    padding-left: 1rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 25%;
  }
`;

const ButtonWrap = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    display: none;
  }
`;

interface OwnProps {
  listTable: (listTableProps: IListTableProps) => React.ReactNode;
  scoreboard: string;
  submitDialog: (
    handleHide: () => void,
    allowNavigation: () => void,
  ) => React.ReactNode;
  title: React.ReactNode;
}

interface IState {
  isSidebarVisible: boolean;
}

type Props = OwnProps & WithTranslation;

const SquadBase = ({
  listTable,
  scoreboard,
  submitDialog,
  t,
  title,
}: Props) => {
  const [isSidebarVisible, setIsSidebarVisible] = useState<boolean>(false);

  const showRef = React.useRef<HTMLButtonElement>(null);

  const hideRef = React.useRef<HTMLButtonElement>(null);

  const handleShowSidebar = () => setIsSidebarVisible(true);

  const handleHideSidebar = () => setIsSidebarVisible(false);

  const handleTransitionEnd = (e: React.TransitionEvent) => {
    // Just handle the Pusher transition
    if (e.target === e.currentTarget) {
      if (isSidebarVisible) {
        hideRef.current?.focus?.();
      } else {
        showRef.current?.focus?.();
      }
    }
  };

  return (
    <PusherWrap>
      <Pusher
        isSidebarVisible={isSidebarVisible}
        onTransitionEnd={handleTransitionEnd}
      >
        <Wrapper>
          <Main>
            <Box mx={2}>
              <PageHeader title={title}>
                <ButtonWrap>
                  <Button
                    ref={showRef}
                    onClick={handleShowSidebar}
                    endIcon={<ControlArrowRight />}
                  >
                    {t("squadBase.playerList", "Player List")}
                  </Button>
                </ButtonWrap>
              </PageHeader>
            </Box>
            <CreateSquad
              listTable={listTable}
              scoreboard={scoreboard}
              showSidebar={handleShowSidebar}
              submitDialog={submitDialog}
            />
            <Fixtures />
          </Main>
          <PusherSecondary>
            <ElementList hideRef={hideRef} hideSidebar={handleHideSidebar} />
          </PusherSecondary>
        </Wrapper>
      </Pusher>
    </PusherWrap>
  );
};

export default withTranslation()(SquadBase);
